import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import Navigation from './components/Navigation';
import { NotificationCenter } from './components/NotificationCenter';
import { SearchBar } from './components/SearchBar';
import BookmarksPage from './pages/BookmarksPage';
import NotesPage from './pages/NotesPage';
import NoteListPage from './pages/NoteListPage';
import NoteDetailPage from './pages/NoteDetailPage';
import NoteEditPage from './pages/NoteEditPage';
import ExpensesPage from './pages/ExpensesPage';
import SchedulePage from './pages/SchedulePage';
import RemindersPage from './pages/RemindersPage';
import SearchResultsPage from './pages/SearchResultsPage';
import CategoriesPage from './pages/CategoriesPage';
import { useNotifications } from './hooks/useNotifications';
import { useNavigate } from 'react-router-dom';

function AuthPage() {
  const [showLogin, setShowLogin] = useState(true);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {showLogin ? (
        <LoginForm onSwitchToRegister={() => setShowLogin(false)} />
      ) : (
        <RegisterForm onSwitchToLogin={() => setShowLogin(true)} />
      )}
    </div>
  );
}

function DashboardContent() {
  const { user, logout } = useAuth();
  const [, setNavOpen] = useState(true);
  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const handleNotificationClick = (notification: any) => {
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar Navigation */}
      <Navigation onToggle={setNavOpen} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4 flex-shrink-0">
          <div className="flex justify-between items-center gap-2 lg:gap-4">
            {/* Mobile: Compact Title */}
            <h1 className="text-lg lg:text-xl font-bold text-gray-900 hidden sm:block">Notes 全能笔记</h1>
            <h1 className="text-lg font-bold text-gray-900 sm:hidden">Notes</h1>

            {/* Search Bar - Hidden on small mobile */}
            <div className="hidden sm:flex flex-1 max-w-2xl">
              <SearchBar placeholder="搜索书签、笔记、日程..." />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 lg:gap-4">
              <NotificationCenter
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onDelete={deleteNotification}
                onNotificationClick={handleNotificationClick}
              />
              {/* User email - Hidden on mobile */}
              <span className="text-sm text-gray-600 hidden lg:inline">{user?.email}</span>
              {/* Logout button */}
              <button
                onClick={logout}
                className="px-3 lg:px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
              >
                <span className="hidden sm:inline">退出登录</span>
                <span className="sm:hidden">退出</span>
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          <div className="sm:hidden mt-3">
            <SearchBar placeholder="搜索..." />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-3 lg:p-6">
          <Routes>
            <Route path="/" element={<Navigate to="/bookmarks" replace />} />
            <Route path="/bookmarks" element={<BookmarksPage />} />
            <Route path="/notes-list" element={<NoteListPage />} />
            <Route path="/notes" element={<NotesPage />} />
            <Route path="/notes/new" element={<NoteEditPage />} />
            <Route path="/notes/:id" element={<NoteDetailPage />} />
            <Route path="/notes/:id/edit" element={<NoteEditPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/reminders" element={<RemindersPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/settings" element={<div className="text-center py-20 text-gray-500">设置页面开发中...</div>} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function Dashboard() {
  return <DashboardContent />;
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? <Dashboard /> : <AuthPage />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
